<!--
// app/components/pages/players-player/HeroPlayerPage.vue
-->
<script setup lang="ts">
import type { PlayerSeasons, SeasonPlayerInfo } from "~/src/player/domain/Player";
import type { Team } from "~/src/team/domain/models/Team";
import type { CountryDTO } from "~/src/country/infrastructure/DTO/CountryDTO";
import DraftPlayerScores from "~/app/components/pages/players-player/DraftPlayerScores.vue";
import nuxtLink from "#app/components/nuxt-link";
import { setAliasUrl, slugify } from "~/src/Shared/utils";
import { useCountries } from "~/app/composables/countries";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useImages } from "~/app/composables/images";
import { useWebContext } from "~/app/composables/webContext";

type Props = {
  player: PlayerSeasons;
  playerSeasonInfo: SeasonPlayerInfo;
  team: Team;
  regionId: number | undefined;
};

const props = defineProps<Props>();

const { t, locale } = useI18n();
const localePath = useLocalePath();
const { getCountry } = useCountries();
const { currentSeason } = useCompetitionStore();
const { playerPlaceholderPath } = useImages();
const { isQueens } = useWebContext();

const defaultColor = "#888888";
const playerCountry = ref<CountryDTO | undefined>();

const draftStatsMapping = player => {
  if (player && player.role !== "goalkeeper") {
    return [
      { name: t("draft.player.physical"), total: player?.metaInformation?.physical || "-" },
      { name: t("draft.player.duels"), total: player?.metaInformation?.duels || "-" },
      { name: t("draft.player.shooting"), total: player?.metaInformation?.shooting || "-" },
      { name: t("draft.player.defence"), total: player?.metaInformation?.defence || "-" },
      { name: t("draft.player.passing"), total: player?.metaInformation?.passing || "-" },
      { name: t("draft.player.skills"), total: player?.metaInformation?.skills || "-" },
    ];
  } else {
    return [
      { name: t("draft.player.reflexes"), total: player?.metaInformation?.reflexes || "-" },
      { name: t("draft.player.diving"), total: player?.metaInformation?.diving || "-" },
      { name: t("draft.player.duels"), total: player?.metaInformation?.duels },
      {
        name: t("draft.player.goalkeeper.passing"),
        total: player?.metaInformation?.goalkeeperPassing || "-",
      },
      { name: t("draft.player.handling"), total: player?.metaInformation?.handling || "-" },
      { name: t("draft.player.anticipation"), total: player?.metaInformation?.anticipation || "-" },
    ];
  }
};

if (props.player && props.player.countryId) {
  playerCountry.value = props.player.country || (await getCountry(props.player.countryId));
}

const regionChanged = ref<boolean | undefined>();
watch(
  () => props.regionId,
  (newValue, oldValue) => {
    if (newValue !== oldValue) regionChanged.value = true;
    else if (newValue === oldValue) regionChanged.value = false;
  }
);

const playerImage = computed(() => {
  if (props.playerSeasonInfo) {
    if (
      props.playerSeasonInfo.season.isCurrent &&
      regionChanged.value === false &&
      props.player &&
      props.player.image &&
      !props.player.currentTeam?.completeName?.includes("Reserve")
    ) {
      return props.player.image.url;
    } else if (props.playerSeasonInfo.playerImage) {
      if (props.playerSeasonInfo.teams.length > 1 && props.player && props.player.currentTeam) {
        const _currentTeam = props.playerSeasonInfo.teams.find(
          team => team.id === props.player.currentTeam?.id
        );

        if (!!_currentTeam) {
          return props.player.image.url;
        } else if (!_currentTeam) {
          return props.playerSeasonInfo.playerImage.url;
        }
      } else {
        return props.playerSeasonInfo.playerImage.url;
      }
    } else {
      return playerPlaceholderPath;
    }
  } else if (props.player && props.player.image) {
    return props.player.image.url;
  } else {
    return playerPlaceholderPath;
  }
});
</script>

<template>
  <div v-if="!!player" class="flex flex-col lg:flex-row relative lg:gap-3 lg:justify-center">
    <div class="p-4 lg:rounded-lg bg-gray-900 lg:min-w-[332px] lg:w-fit relative overflow-hidden">
      <div class="kql-clip-diagonal-wrapper">
        <div
          class="kql-clip-diagonal-right-lg"
          :style="`background-image: linear-gradient(180deg, ${team.firstColorHEX ? team.firstColorHEX : defaultColor}99 0%, ${team.secondColorHEX ? team.secondColorHEX : defaultColor}00 100%);`"
        ></div>
        <div
          class="kql-clip-diagonal-right-mbl"
          :style="`background-image: linear-gradient(90deg, ${team.firstColorHEX ? team.firstColorHEX : defaultColor}99 0%, ${team.secondColorHEX ? team.secondColorHEX : defaultColor}00 100%);`"
        ></div>
      </div>
      <div class="kql-container-player-img">
        <NuxtImg
          :src="setAliasUrl(playerImage)"
          :alt="player?.shortName || ''"
          width="456"
          height="642"
          sizes="150px sm:300px"
          fit="outside"
          class="max-h-[423px] object-top object-contain"
        />
        <NuxtImg
          v-if="team.logo?.url"
          :src="setAliasUrl(team.logo?.url)"
          :alt="team.completeName || ''"
          width="80"
          height="80"
          class="absolute top-2 left-2 w-20 hidden lg:block lg:right-auto"
        />

        <div class="kql-player-number" v-if="player && player.jersey">#{{ player.jersey }}</div>
      </div>
    </div>
    <div class="p-4 lg:p-8 lg:rounded-lg bg-gray-900 w-full lg:flex lg:flex-col lg:justify-between">
      <div class="flex justify-between">
        <div>
          <h1 class="text-2xl font-bold lg:font-4xl">{{ player?.shortName }}</h1>
          <p class="text-gray-400 text-sm lg:text-base first-letter:capitalize">
            {{ $t(`${player?.role}`) }}
          </p>
          <!--          <div v-if="player?.marketValue && regionId !== 5" class="kql-player-info-budget">
            <p>
              {{ $t("players.card.clause") }}: <span class="font-bold">{{ player.marketValue }} M</span>
            </p>
          </div>-->
          <div class="flex gap-4 mt-6" v-if="player && (player.currentTeam || playerSeasonInfo?.team.name)">
            <p class="kql-player-info-title">
              {{ $t("player.team.word") }}
            </p>
            <component
              :is="playerSeasonInfo.season.id === 8 ? 'p' : nuxtLink"
              v-if="player && (player.currentTeam || playerSeasonInfo?.team.name) && team"
              :to="
                localePath({
                  name: 'teams-team-teamName',
                  params: {
                    team: team.id,
                    teamName: slugify(team.completeName),
                  },
                })
              "
              :class="['kql-player-info-value', { underline: playerSeasonInfo.season.id !== 8 }]"
              >{{ team.completeName }}</component
            >
          </div>
          <div class="flex gap-4" v-if="player && player.height">
            <p class="kql-player-info-title">{{ $t("player.height.word") }}</p>
            <p class="kql-player-info-value">{{ player.height }}cm</p>
          </div>
          <div class="flex gap-4" v-if="player && player.birthDate">
            <p class="kql-player-info-title">{{ $t("player.birthDate.word") }}</p>
            <p class="kql-player-info-value">
              {{
                new Date(player.birthDate).toLocaleDateString(locale, {
                  year: "numeric",
                  day: "2-digit",
                  month: "2-digit",
                })
              }}
            </p>
          </div>
          <div class="flex gap-4" v-if="player && player.countryId && playerCountry">
            <p class="kql-player-info-title">{{ $t("player.country.word") }}</p>
            <p class="kql-player-info-value">{{ playerCountry.name }}</p>
          </div>
        </div>
        <div
          class="rounded-lg px-1 py-1 lg:px-2 lg:py-3 bg-primary flex justify-between items-center flex-col min-w-[46px] lg:min-w-[66px] text-black h-fit absolute lg:static top-2 ltr:right-4 rtl:left-4"
        >
          <p class="text-[10px] text-black">{{ $t("players.card.average.title") }}</p>
          <p class="text-base font-bold">
            <span v-if="player && player.metaInformation && player.metaInformation.average">
              {{ player.metaInformation.average }}
            </span>
            <span v-else> - </span>
          </p>
        </div>
        <NuxtImg
          v-if="team.logo.url"
          :src="setAliasUrl(team.logo.url)"
          :alt="team.completeName || ''"
          width="48"
          height="48"
          class="absolute top-16 ltr:right-4 rtl:left-4 w-12 block lg:hidden"
        />
      </div>

      <draft-player-scores :draft-scores="draftStatsMapping(player)"></draft-player-scores>
    </div>
  </div>
</template>

<style scoped>
.kql-container-player-img {
  @apply lg:shrink-0 relative rounded bg-[url('/img/bg-card/bg-card-president-team-page.jpg')] bg-cover bg-center bg-no-repeat lg:max-w-[300px] max-w-[150px] lg:h-full overflow-hidden z-10;
}

.kql-player-number {
  @apply text-sm lg:text-base font-bold px-2 lg:px-4 py-1 bg-gray-800 rounded-full bg-opacity-60 absolute bottom-2 right-2 lg:bottom-4 lg:right-4 z-[3];
}

.kql-player-info-value {
  @apply text-sm lg:text-base;
}

.kql-player-info-title {
  @apply text-sm lg:text-base text-gray-600;
}

.kql-player-info-budget {
  @apply text-black text-xs text-center bg-primary rounded max-w-max mt-1 px-1 py-0.5;
}

.kql-clip-diagonal-right-lg {
  @apply hidden lg:block w-44 h-44 absolute left-0 top-0;
  clip-path: polygon(0 0, 100% 0%, 0% 100%, 0 100%);
}

.kql-clip-diagonal-right-mbl {
  @apply lg:hidden block w-full h-28 absolute left-0 top-0 z-0 rtl:-scale-x-100;
  clip-path: polygon(0 0, 100% 0%, 80% 100%, 0 100%);
}
</style>
